/* HOTiTi v9. Product photographs animate; navigation, text and commerce stay stable. */
(() => {
  if (window.hotitiMotion) return;
  const root = document.documentElement;
  const body = document.body;
  const enabled = body.dataset.htMotionEnabled === 'true';
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const key = 'hotiti-motion-paused';
  let paused = false;
  try { paused = sessionStorage.getItem(key) === 'true'; } catch (_) { /* Storage is optional. */ }
  const loops = new Map();
  const books = new Map();
  const features = new Map();
  const stacks = new Map();
  const rings = new Map();
  const regions = new Set();
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => { entry.target.dataset.htmVisible = String(entry.isIntersecting); });
    syncBooks();
    syncProductMotion();
  }, { rootMargin: '80px' }) : null;

  function syncState() {
    const state = !enabled ? 'off' : media.matches ? 'reduced' : paused ? 'paused' : 'running';
    root.dataset.htMotion = state;
    document.querySelectorAll('[data-htm-toggle]').forEach(button => {
      button.hidden = !enabled;
      button.disabled = media.matches;
      button.setAttribute('aria-pressed', String(state !== 'running'));
      button.querySelector('[data-htm-toggle-label]').textContent = media.matches ? button.dataset.labelReduced : paused ? button.dataset.labelPlay : button.dataset.labelPause;
    });
    syncBooks();
    syncProductMotion();
  }
  function clearProductAnimations(state) {
    state.animations.forEach(animation => { animation.onfinish = null; animation.cancel(); });
    state.animations = [];
  }
  function prepareFeature(state) {
    state.prepared = true;
    state.element.dataset.htmPrepared = 'true';
    const generation = ++state.generation;
    clearProductAnimations(state);
    const previous = bookImage(state, state.base, state.index + state.images.length - 1);
    const incoming = bookImage(state, state.incoming, state.index);
    const directions = ['translate(-101%,0)', 'translate(101%,0)', 'translate(0,-101%)', 'translate(0,101%)'];
    const start = directions[state.step % directions.length];
    state.incoming.style.transform = start;
    const scale = Number(state.element.dataset.htmScale) || 2.8;
    incoming.style.transform = `scale(${scale})`;
    state.incoming.hidden = false;
    Promise.all([previous, incoming].map(image => image.decode ? image.decode().catch(() => {}) : Promise.resolve())).then(() => {
      if (!state.element.isConnected || generation !== state.generation) return;
      const tempo = parseFloat(getComputedStyle(body).getPropertyValue('--htm-tempo')) || 1;
      const options = { duration: (Number(state.element.dataset.htmDuration) || 3800) * tempo, fill: 'both' };
      const easing = 'cubic-bezier(.215,.61,.355,1)';
      const gallery = state.element.hasAttribute('data-htm-gallery');
      const arrival = state.incoming.animate([
        { transform: start, offset: 0 },
        { transform: start, offset: gallery ? 0.35 : 0, easing },
        { transform: 'translate(0,0)', offset: gallery ? 0.55 : 0.22 },
        { transform: 'translate(0,0)', offset: 1 }
      ], options);
      const zoom = incoming.animate([
        { transform: `scale(${scale})`, offset: 0 },
        { transform: `scale(${scale})`, offset: gallery ? 0.4 : 0.06, easing },
        { transform: 'scale(1)', offset: gallery ? 0.78 : 0.54 },
        { transform: 'scale(1)', offset: 1 }
      ], options);
      const fade = state.base.animate([
        { opacity: 1, offset: 0 },
        { opacity: 1, offset: gallery ? 0.35 : 0, easing },
        { opacity: 0, offset: gallery ? 0.6 : 0.24 },
        { opacity: 0, offset: 1 }
      ], options);
      state.animations = [arrival, zoom, fade];
      arrival.onfinish = () => {
        state.index = (state.index + 1) % state.images.length;
        state.step += 1;
        prepareFeature(state);
      };
      syncProductMotion();
    });
  }
  function prepareStack(state) {
    const count = state.cards.length;
    const tempo = parseFloat(getComputedStyle(body).getPropertyValue('--htm-tempo')) || 1;
    const direction = Number(state.element.dataset.htmDirection) < 0 ? -1 : 1;
    const phase = Number(state.element.dataset.htmPhase) || 0;
    const exit = `translate(${direction * 135}%,-12%) rotate(${direction * 15}deg)`;
    const behind = `translate(${direction * 6}%,3%) rotate(${direction * 5}deg)`;
    const waiting = `translate(${-direction * 4}%,2%) rotate(${-direction * 4}deg)`;
    state.animations = state.cards.map((card, index) => card.animate([
      { transform: 'translate(0,0) rotate(0deg)', zIndex: 9, opacity: 1, offset: 0 },
      { transform: 'translate(0,0) rotate(0deg)', zIndex: 9, opacity: 1, offset: 0.49 / count },
      { transform: exit, zIndex: 9, opacity: 1, offset: 0.77 / count },
      { transform: exit, zIndex: 9, opacity: 0, offset: 0.945 / count },
      { transform: behind, zIndex: 1, opacity: 0, offset: 1 / count },
      { transform: behind, zIndex: 1, opacity: 1, offset: 1.12 / count },
      { transform: behind, zIndex: 1, opacity: 1, offset: Math.max(1.12 / count, 1 - 1.12 / count) },
      { transform: waiting, zIndex: 2, opacity: 1, offset: 1 - 0.28 / count },
      { transform: waiting, zIndex: 2, opacity: 1, offset: 1 }
    ], { duration: count * 2800 * tempo, delay: (-((count - index) % count) * 2800 - phase) * tempo, iterations: Infinity }));
    state.prepared = true;
  }
  function syncProductMotion() {
    for (const state of [...features.values(), ...stacks.values(), ...rings.values()]) {
      if (!enabled || media.matches) {
        if (state.prepared) {
          state.generation += 1;
          clearProductAnimations(state);
          state.prepared = false;
          if (state.kind === 'feature') {
            bookImage(state, state.base, state.index);
            state.incoming.hidden = true;
            state.element.removeAttribute('data-htm-prepared');
          }
        }
        continue;
      }
      const running = !paused && !document.hidden && state.element.dataset.htmVisible !== 'false';
      if (!state.prepared) {
        if (running) {
          if (state.kind === 'feature') prepareFeature(state);
          else if (state.kind === 'ring') prepareRing(state);
          else prepareStack(state);
        }
        continue;
      }
      state.animations.forEach(animation => {
        if (running && animation.playState === 'paused') animation.play();
        else if (!running && animation.playState !== 'paused') animation.pause();
      });
    }
  }
  function prepareRing(state) {
    const count = state.cards.length;
    const tempo = parseFloat(getComputedStyle(body).getPropertyValue('--htm-tempo')) || 1;
    const frames = [];
    for (let index = 0; index < count; index++) {
      const transform = `rotateY(${-index * 360 / count}deg)`;
      frames.push({ transform, offset: index / count });
      frames.push({ transform, offset: (index + 0.56) / count, easing: 'ease-in-out' });
    }
    frames.push({ transform: 'rotateY(-360deg)', offset: 1 });
    state.animations = [state.rotor.animate(frames, { duration: count * 2000 * tempo, iterations: Infinity })];
    state.prepared = true;
  }
  function setupProductMotion(scope) {
    if (!Element.prototype.animate) return;
    scope.querySelectorAll('[data-htm-feature]').forEach(element => {
      if (features.has(element)) return;
      const images = [...element.querySelector('[data-htm-feature-images]').content.querySelectorAll('img')];
      if (images.length < 2) return;
      features.set(element, {
        kind: 'feature', element, images, index: element.hasAttribute('data-htm-gallery') ? 1 : 0, step: Number(element.dataset.htmStep) || 0, generation: 0, prepared: false, animations: [],
        base: element.querySelector('.htm-feature-base'), incoming: element.querySelector('.htm-feature-incoming')
      });
    });
    scope.querySelectorAll('[data-htm-stack]').forEach(element => {
      if (stacks.has(element)) return;
      const cards = [...element.querySelectorAll('.htm-stack-card')];
      if (cards.length < 2) return;
      stacks.set(element, { kind: 'stack', element, cards, generation: 0, prepared: false, animations: [] });
    });
    scope.querySelectorAll('[data-htm-ring]').forEach(element => {
      if (rings.has(element)) return;
      const cards = [...element.querySelectorAll('.htm-ring-photo')];
      const position = element.querySelector('.htm-ring-position');
      const resize = () => {
        const scale = element.clientWidth / 540;
        position.style.width = `${180 * scale}px`;
        position.style.height = `${254 * scale}px`;
        position.style.perspective = `${950 * scale}px`;
        position.style.setProperty('--htm-ring-radius', `${204 * scale}px`);
      };
      resize();
      const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(resize) : null;
      resizeObserver?.observe(element);
      if (!resizeObserver) window.addEventListener('resize', resize);
      rings.set(element, { kind: 'ring', element, cards, rotor: element.querySelector('.htm-ring-rotor'), generation: 0, prepared: cards.length < 2, animations: [], resizeObserver, resize });
    });
  }
  function bookImage(book, slot, index) {
    const image = book.images[index % book.images.length].cloneNode(true);
    image.loading = 'eager';
    slot.replaceChildren(image);
    return image;
  }
  function clearBookAnimation(book) {
    if (book.animation) {
      book.animation.onfinish = null;
      book.animation.cancel();
      book.animation = null;
    }
  }
  function staticBook(book) {
    book.generation += 1;
    clearBookAnimation(book);
    bookImage(book, book.left, book.index);
    bookImage(book, book.right, book.index + 1);
    book.leaf.hidden = true;
    book.prepared = false;
  }
  function prepareBook(book) {
    book.prepared = true;
    const generation = ++book.generation;
    // The right page turns onto the left; the next spread is already underneath.
    const images = [
      bookImage(book, book.left, book.index),
      bookImage(book, book.right, book.index + 3),
      bookImage(book, book.front, book.index + 1),
      bookImage(book, book.back, book.index + 2)
    ];
    clearBookAnimation(book);
    book.leaf.hidden = false;
    Promise.all(images.map(image => image.decode ? image.decode().catch(() => {}) : Promise.resolve())).then(() => {
      if (!book.element.isConnected || book.generation !== generation) return;
      const tempo = parseFloat(getComputedStyle(body).getPropertyValue('--htm-tempo')) || 1;
      book.animation = book.leaf.animate([
        { transform: 'rotateY(0deg)', offset: 0 },
        { transform: 'rotateY(0deg)', offset: 0.44, easing: 'cubic-bezier(.45,0,.2,1)' },
        { transform: 'rotateY(-180deg)', offset: 0.88 },
        { transform: 'rotateY(-180deg)', offset: 1 }
      ], { duration: 3100 * tempo, fill: 'both' });
      book.animation.onfinish = () => {
        book.index = (book.index + 2) % book.images.length;
        prepareBook(book);
      };
      syncBooks();
    });
  }
  function syncBooks() {
    books.forEach(book => {
      if (!enabled || media.matches) {
        if (book.prepared) staticBook(book);
        return;
      }
      if (!book.prepared) {
        if (!paused && !document.hidden && book.region?.dataset.htmVisible !== 'false') prepareBook(book);
        return;
      }
      if (!book.animation) return;
      const running = !paused && !document.hidden && book.region?.dataset.htmVisible !== 'false';
      if (running && book.animation.playState === 'paused') book.animation.play();
      else if (!running && book.animation.playState !== 'paused') book.animation.pause();
    });
  }
  function setupBook(element) {
    if (books.has(element) || !Element.prototype.animate) return;
    const images = [...(element.querySelector('[data-htm-book-images]')?.content.querySelectorAll('img') || [])];
    if (images.length < 2) return;
    books.set(element, {
      element, images, index: 0, generation: 0, animation: null, prepared: false,
      region: element.closest('[data-htm-region]'),
      left: element.querySelector('.htm-book-left'), right: element.querySelector('.htm-book-right'),
      leaf: element.querySelector('.htm-book-leaf'), front: element.querySelector('.htm-book-front'),
      back: element.querySelector('.htm-book-back')
    });
  }
  function decorativeCopy(node, attribute) {
    const copy = node.cloneNode(true);
    copy.setAttribute(attribute, '');
    copy.setAttribute('aria-hidden', 'true');
    copy.removeAttribute('data-htm-original');
    for (const element of [copy, ...copy.querySelectorAll('*')]) {
      element.removeAttribute('id');
      element.removeAttribute('data-shopify-editor-block');
      if (element.matches('a,button,input,select,textarea,[tabindex]')) element.setAttribute('tabindex', '-1');
    }
    return copy;
  }
  function buildLoop(viewport) {
    if (!viewport.isConnected || !viewport.clientWidth) return;
    const track = viewport.querySelector('.htm-track');
    const original = viewport.querySelector('[data-htm-original]');
    if (!track || !original || !original.children.length) return;
    track.querySelectorAll(':scope > [data-htm-clone]').forEach(el => el.remove());
    original.querySelectorAll(':scope > [data-htm-extra]').forEach(el => el.remove());
    viewport.removeAttribute('data-htm-ready');
    if (!enabled || media.matches || paused || viewport.classList.contains('htm-keyboard')) return;
    viewport.dataset.htmReady = 'true';
    const pieces = [...original.children];
    let additions = 0;
    while (original.getBoundingClientRect().width < viewport.clientWidth + 30 && additions < 20) {
      pieces.forEach(piece => original.append(decorativeCopy(piece, 'data-htm-extra')));
      additions += 1;
    }
    viewport.style.setProperty('--htm-duration', `${34 * original.children.length / 4}s`);
    track.append(decorativeCopy(original, 'data-htm-clone'));
  }
  function setup(scope = document) {
    setupProductMotion(scope);
    scope.querySelectorAll('[data-htm-book]').forEach(setupBook);
    scope.querySelectorAll('[data-htm-region]').forEach(region => {
      if (regions.has(region)) return;
      regions.add(region);
      observer?.observe(region);
    });
    scope.querySelectorAll('[data-htm-loop]').forEach(viewport => {
      if (loops.has(viewport)) return;
      let scheduled = 0;
      const queueBuild = () => {
        cancelAnimationFrame(scheduled);
        scheduled = requestAnimationFrame(() => buildLoop(viewport));
      };
      const resize = 'ResizeObserver' in window ? new ResizeObserver(queueBuild) : null;
      resize?.observe(viewport);
      viewport.addEventListener('focusin', event => {
        if (event.target.closest('[data-htm-original]')) viewport.classList.add('htm-keyboard');
      });
      viewport.addEventListener('focusout', event => {
        if (!viewport.contains(event.relatedTarget)) {
          viewport.classList.remove('htm-keyboard');
          viewport.scrollLeft = 0;
          queueBuild();
        }
      });
      loops.set(viewport, { resize, cancel: () => cancelAnimationFrame(scheduled) });
      buildLoop(viewport);
    });
    syncState();
  }
  function cleanRemoved() {
    for (const states of [features, stacks, rings]) for (const [element, state] of states) if (!element.isConnected) {
      state.generation += 1;
      clearProductAnimations(state);
      state.resizeObserver?.disconnect();
      if (state.resize) window.removeEventListener('resize', state.resize);
      states.delete(element);
    }
    for (const [element, book] of books) if (!element.isConnected) {
      book.generation += 1;
      clearBookAnimation(book);
      books.delete(element);
    }
    for (const [viewport, resources] of loops) if (!viewport.isConnected) {
      resources.resize?.disconnect(); resources.cancel(); loops.delete(viewport);
    }
    for (const region of regions) if (!region.isConnected) { observer?.unobserve(region); regions.delete(region); }
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-htm-toggle]');
    if (!button || button.disabled) return;
    paused = !paused;
    try { sessionStorage.setItem(key, String(paused)); } catch (_) { /* Storage is optional. */ }
    syncState();
    loops.forEach((_, viewport) => buildLoop(viewport));
  });
  document.addEventListener('visibilitychange', () => {
    root.toggleAttribute('data-ht-motion-hidden', document.hidden);
    syncBooks();
    syncProductMotion();
  });
  const preferenceChanged = () => { syncState(); loops.forEach((_, viewport) => buildLoop(viewport)); };
  if (media.addEventListener) media.addEventListener('change', preferenceChanged);
  else media.addListener(preferenceChanged);
  document.addEventListener('shopify:section:load', () => { cleanRemoved(); setup(); });
  document.addEventListener('shopify:section:unload', () => { requestAnimationFrame(cleanRemoved); });
  document.addEventListener('shopify:block:select', event => {
    const loop = event.target.closest('[data-htm-loop]');
    if (loop) { loop.classList.add('htm-keyboard'); event.target.scrollIntoView({ block: 'nearest', inline: 'center' }); }
  });
  document.addEventListener('shopify:block:deselect', event => { event.target.closest('[data-htm-loop]')?.classList.remove('htm-keyboard'); });
  window.hotitiMotion = { refresh: () => { cleanRemoved(); setup(); } };
  syncState(); setup();
})();
